import { useState } from "react";
import { ApiError, type Goal, api, label } from "../api.js";
import { type ExampleSeed, ExamplesScreen } from "./Examples.js";

/** Список целей и заведение новой. Критерий и правило доказательства пишутся ДО запуска. */

type Props = { goals: Goal[]; onChanged: () => void; onOpenGoal: (id: string) => void };

/* Значения словарей, подписи — из общего LABEL: держать вторую копию подписей значит
   однажды показать «проект» на одном экране и «project» на другом. */
const TYPES = ["habit", "project", "skill", "quit", "performance", "exploration"] as const;
const HORIZONS = ["week", "month", "quarter", "year_plus"] as const;
const DOMAINS = ["work", "sport", "family", "personal", "study"] as const;

export function GoalsScreen({ goals, onChanged, onOpenGoal }: Props): React.JSX.Element {
  const [adding, setAdding] = useState(false);
  const [showing, setShowing] = useState<"goals" | "examples">("goals");
  const [seed, setSeed] = useState<ExampleSeed | null>(null);

  return (
    <>
      <header className="head">
        <h1>{showing === "examples" ? "Примеры" : "Цели"}</h1>
        <div className="head-acts">
          {/* Примеры стоят рядом с «Новой» намеренно: они нужны ровно в ту минуту,
              когда цель заводят и не знают, как сформулировать критерий. */}
          <button
            className="btn"
            onClick={() => setShowing((value) => (value === "examples" ? "goals" : "examples"))}
          >
            {showing === "examples" ? "Закрыть" : "Примеры"}
          </button>
          {showing !== "examples" && (
            <button className="btn" onClick={() => setAdding((value) => !value)}>
              {!adding && (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
                     strokeLinecap="round" aria-hidden="true">
                  <path d="M12 5v14" />
                  <path d="M5 12h14" />
                </svg>
              )}
              {adding ? "Отмена" : "Новая"}
            </button>
          )}
        </div>
      </header>

      {showing === "examples" && (
        <ExamplesScreen
          onUse={(next) => {
            // Пример закрывается сам: библиотека нужна до формы, а не рядом с ней.
            setSeed(next);
            setShowing("goals");
            setAdding(true);
          }}
        />
      )}

      {showing === "goals" && adding && (
        <NewGoalForm
          seed={seed}
          onDone={() => {
            setAdding(false);
            setSeed(null);
            onChanged();
          }}
        />
      )}

      {showing === "goals" && goals.length === 0 && !adding && <p className="empty">Целей пока нет.</p>}

      {showing === "goals" && goals.length > 0 && <div className="rows">
        {goals.map((goal) => (
          <button className="row" key={goal.id} onClick={() => onOpenGoal(goal.id)}>
            <span className="two-line">
              {goal.title}
              <span className="hint">
                {label(goal.horizon_bucket)} · {label(goal.domain)}
                {goal.days_since_touch !== null && goal.days_since_touch !== undefined
                  ? ` · касание ${goal.days_since_touch} дн. назад`
                  : ""}
              </span>
            </span>
            <span className="sub">
              {label(goal.status)}
              <svg width="8" height="13" viewBox="0 0 9 15" fill="none" stroke="rgba(235,235,245,0.4)" strokeWidth="2"
                   strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M1.5 1.5L7.5 7.5L1.5 13.5" />
              </svg>
            </span>
          </button>
        ))}
      </div>}

      {showing === "goals" && <Chains goals={goals} onOpenGoal={onOpenGoal} />}
    </>
  );
}

/** «1 цель», «3 цели», «5 целей» — число без своего слова читается как код. */
function plural(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 14) return "целей";
  const ones = n % 10;
  if (ones === 1) return "цель";
  if (ones >= 2 && ones <= 4) return "цели";
  return "целей";
}

/**
 * Цепочка: длинная цель и короткие, которые к ней ведут (решение 3 CONCEPT — иерархия
 * в обе стороны). Уровень ровно один: звено не может быть родителем, это правило кода.
 *
 * Длинная цель живёт по обычным правилам — свой критерий, своё правило доказательства,
 * свой прогноз. Закрытые короткие идут ей контекстом, а не зачётом: цепочка не является
 * прогресс-баром и счётчиком не становится.
 */
function Chains({ goals, onOpenGoal }: { goals: Goal[]; onOpenGoal: (id: string) => void }): React.JSX.Element | null {
  const children = new Map<string, number>();
  for (const goal of goals) {
    if (!goal.parent_goal_id) continue;
    children.set(goal.parent_goal_id, (children.get(goal.parent_goal_id) ?? 0) + 1);
  }
  const parents = goals.filter((goal) => children.has(goal.id));
  if (parents.length === 0) return null;

  return (
    <>
      <div className="sec">Цепочка</div>
      <section className="rows">
        {parents.map((goal) => (
          <button className="row" key={goal.id} onClick={() => onOpenGoal(goal.id)}>
            <span className="two-line">
              {goal.title}
              <span className="hint">
                {label(goal.horizon_bucket)} · {label(goal.domain)}
                {goal.chain_declared_at ? ` · объявлена ${goal.chain_declared_at.slice(0, 10)}` : ""}
              </span>
            </span>
            <span className="sub">
              <span className="num">{children.get(goal.id)}</span> {plural(children.get(goal.id) ?? 0)}
              <svg width="8" height="13" viewBox="0 0 9 15" fill="none" stroke="rgba(235,235,245,0.4)" strokeWidth="2"
                   strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M1.5 1.5L7.5 7.5L1.5 13.5" />
              </svg>
            </span>
          </button>
        ))}
      </section>
      <p className="note-under">
        Длинная цель живёт по обычным правилам: свой критерий, своё правило доказательства,
        свой прогноз вперёд. Закрытые короткие идут ей контекстом, а не зачётом.
      </p>
    </>
  );
}

/**
 * `seed` приходит из примера корпуса и заполняет ОСИ и форму правила.
 *
 * ⚠️ Название и критерий не переносятся никогда. Подставленный чужой критерий легко
 * оставить как есть — и цель окажется не твоей, а критерий запирается при запуске
 * и правится потом только ревизией. Пример показывает способ, формулировку пишет владелец.
 */
function NewGoalForm({ seed, onDone }: { seed?: ExampleSeed | null; onDone: () => void }): React.JSX.Element {
  const [form, setForm] = useState({
    title: "",
    why: "",
    goal_type: seed?.goal_type ?? "performance",
    horizon_bucket: seed?.horizon_bucket ?? "month",
    domain: seed?.domain ?? "sport",
    target_date: "",
    success_criterion: "",
    proof_rule: "",
    reward: "",
    metric: seed?.metric ?? "",
    op: seed?.op ?? ">=",
    value: seed?.value ?? "",
    prob: ""
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const machine = form.metric && form.value !== "";
      const created = await api.post<{ goal: Goal }>("/api/goals", {
        title: form.title,
        why: form.why || undefined,
        goal_type: form.goal_type,
        horizon_bucket: form.horizon_bucket,
        domain: form.domain,
        target_date: form.target_date || undefined,
        success_criterion: form.success_criterion,
        proof_rule: form.proof_rule,
        proof_machine_checkable: Boolean(machine),
        reward: form.reward || undefined
      });

      if (machine) {
        await api.post(`/api/goals/${created.goal.id}/machine-rule`, {
          metric: form.metric,
          op: form.op,
          value: Number(form.value.replace(",", "."))
        });
      }
      if (form.prob && form.target_date) {
        await api.post("/api/forecasts", {
          subject_type: "goal",
          subject_id: created.goal.id,
          question: `Будет ли критерий выполнен к ${form.target_date}?`,
          kind: "binary",
          prob: Number(form.prob) / 100,
          resolve_by: form.target_date
        });
      }
      onDone();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "не сохранилось");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card form">
      <label>
        Цель
        <input value={form.title} onChange={set("title")} placeholder="Пробежать 10 км без остановки" />
      </label>
      <label>
        Зачем
        <input value={form.why} onChange={set("why")} placeholder="личная причина" />
      </label>

      <div className="triple">
        <label>
          Тип
          <select value={form.goal_type} onChange={set("goal_type")}>
            {TYPES.map((value) => (
              <option key={value} value={value}>{label(value)}</option>
            ))}
          </select>
        </label>
        <label>
          Горизонт
          <select value={form.horizon_bucket} onChange={set("horizon_bucket")}>
            {HORIZONS.map((value) => (
              <option key={value} value={value}>{label(value)}</option>
            ))}
          </select>
        </label>
        <label>
          Сфера
          <select value={form.domain} onChange={set("domain")}>
            {DOMAINS.map((value) => (
              <option key={value} value={value}>{label(value)}</option>
            ))}
          </select>
        </label>
      </div>

      <label>
        Критерий успеха <span className="req">запирается при запуске</span>
        <input value={form.success_criterion} onChange={set("success_criterion")} placeholder="10,0 км без остановки" />
      </label>
      <label>
        Правило доказательства
        <input value={form.proof_rule} onChange={set("proof_rule")} placeholder="запись с непрерывным сегментом" />
      </label>

      <div className="triple">
        <label>
          Метрика для кода
          <input value={form.metric} onChange={set("metric")} placeholder="distance_km" />
        </label>
        <label>
          Условие
          <select value={form.op} onChange={set("op")}>
            {[">=", ">", "<=", "<", "=="].map((op) => (
              <option key={op} value={op}>{op}</option>
            ))}
          </select>
        </label>
        <label>
          Значение
          <input value={form.value} onChange={set("value")} inputMode="decimal" placeholder="10" />
        </label>
      </div>
      <p className="hint">Заполнишь эти три — судить будет код, а не человек.</p>

      <div className="triple">
        <label>
          Срок
          <input type="date" value={form.target_date} onChange={set("target_date")} />
        </label>
        <label>
          Твой прогноз, %
          <input value={form.prob} onChange={set("prob")} inputMode="numeric" placeholder="70" />
        </label>
        <label>
          Награда
          <input value={form.reward} onChange={set("reward")} placeholder="кроссовки" />
        </label>
      </div>

      {error && <p className="error">{error}</p>}
      <button className="btn primary" disabled={busy} onClick={() => void submit()}>
        {busy ? "…" : "Завести"}
      </button>
    </section>
  );
}
